import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Configuration, OrderPredictionsApi } from '@/api';
import Button, { Variant } from '@/components/Button';
import CartLottieIcon from '@/components/icons/CartLottieIcon';
import SpinnerIcon from '@/components/icons/SpinnerIcon';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import { useCart } from '@/modules/store/contexts/CartContext';
import AutoOrderService from '@/modules/store/services/AutoOrderService';
import { delay } from '@/utils/async';

interface AutoOrderButtonProps {
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
  variant?: Variant;
  children?: ReactNode;
  className?: string;
  loadingIconClassName?: string;
}

const AutoOrderButton = ({ children, onSuccess, onError, ...props }: AutoOrderButtonProps) => {
  const { resetCart, addToCart } = useCart();
  const [available, setAvailable] = useState<boolean | null>(null);

  const auth = useAuth();

  const storeId = auth.currentUser().storeId;

  const autoOrderService = useMemo(
    () => new AutoOrderService(new OrderPredictionsApi(new Configuration(auth))),
    [auth],
  );

  useEffect(() => {
    if (!storeId) {
      setAvailable(false);
      return;
    }

    const checkAvailability = async () => {
      try {
        const result = await autoOrderService.isPredictionAvailable();
        setAvailable(result);
      } catch (err) {
        console.error('Failed to check auto-order availability', err);
        setAvailable(false);
      }
    };

    void checkAvailability();
  }, [autoOrderService, storeId]);

  const handleClick = async () => {
    try {
      resetCart();

      const predictOrders = autoOrderService.predictOrders();
      const minimumDelay = delay(1_000);

      const carts = await Promise.all([predictOrders, minimumDelay]).then(([result]) => result);

      for (const cart of carts) {
        for (const item of cart.items) {
          addToCart(cart.supplierId, item);
        }
      }

      onSuccess?.();
    } catch (error) {
      console.error('Error predicting orders:', error);
      onError?.(error);
    }
  };

  if (!storeId || available === null || !available) {
    return null;
  }

  return (
    <Button
      {...props}
      onClick={handleClick}
      icon={
        <div className="-ml-1 -mb-0.5 mr-0.5 w-10">
          <CartLottieIcon />
        </div>
      }
      loadingIcon={
        <div className="flex -ml-1 mr-0.5 w-10 pl-1 items-center justify-center">
          <SpinnerIcon className="w-6 h-6 fill-green-800" />
        </div>
      }
      rounded="full"
      className="w-full sm:w-auto min-h-12 min-w-48 font-bold text-md py-1"
    >
      {children ?? 'Auto-Order'}
    </Button>
  );
};

export default AutoOrderButton;
